import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { BlockSkeleton } from "@/components/Loading";
import { StatusBadge } from "@/components/StatusBadge";
import { BrandSelectField } from "@/components/BrandSelectField";
import { ExportButton } from "@/components/ExportButton";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const STATUSES = ["booked", "seated", "completed", "cancelled", "no_show"];
const SOURCES = ["website", "phone", "walk-in", "opentable"];

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const localToIso = (local) => (local ? new Date(local).toISOString() : "");

const empty = {
  brand_id: "",
  guest_name: "",
  guest_email: "",
  guest_phone: "",
  party_size: 2,
  reservation_date: "",
  status: "booked",
  source: "website",
  notes: "",
};

export default function Reservations() {
  const { queryParams } = useFilters();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { ...queryParams };
    if (statusFilter !== "all") params.status = statusFilter;
    api
      .get("/reservations", { params })
      .then((res) => setItems(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [queryParams, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({ ...empty, reservation_date: isoToLocal(new Date().toISOString()) });
    setEditingId(null);
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setForm({ ...r, reservation_date: isoToLocal(r.reservation_date) });
    setEditingId(r.id);
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        party_size: Number(form.party_size) || 1,
        reservation_date: localToIso(form.reservation_date),
      };
      if (editingId) {
        await api.patch(`/reservations/${editingId}`, payload);
        toast.success("Reservation updated");
      } else {
        await api.post("/reservations", payload);
        toast.success("Reservation created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/reservations/${deleteId}`);
      toast.success("Reservation deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <PageHeader title="Reservations" subtitle="Manage bookings across the selected period.">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="reservations-status-filter" className="h-9 w-[150px] rounded-full border-[var(--pb-border)] bg-white text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s.replace("_", " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ExportButton resource="reservations" params={{ ...queryParams, ...(statusFilter !== "all" ? { status: statusFilter } : {}) }} />
        <Button data-testid="add-reservation-button" onClick={openCreate} className="h-9 gap-2 rounded-full bg-[#18344A] text-white hover:bg-[#18344A]/90">
          <Plus className="h-4 w-4" /> Add reservation
        </Button>
      </PageHeader>

      <Card className="overflow-hidden rounded-[14px] border-[var(--pb-border)] bg-white shadow-[var(--pb-shadow-sm)]">
        {loading ? (
          <div className="p-4"><BlockSkeleton className="h-96" /></div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No reservations found"
            message="Adjust filters or add a new reservation."
            icon={CalendarClock}
            action={<Button onClick={openCreate} className="rounded-full bg-[#18344A] text-white hover:bg-[#18344A]/90"><Plus className="mr-2 h-4 w-4" />Add reservation</Button>}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="reservations-table">
              <TableHeader>
                <TableRow className="bg-[rgba(24,52,74,0.04)]">
                  <TableHead>Guest</TableHead>
                  <TableHead>Date &amp; time</TableHead>
                  <TableHead className="text-right">Party</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} className="hover:bg-[rgba(183,165,142,0.1)]">
                    <TableCell>
                      <div className="font-medium text-[#30383C]">{r.guest_name}</div>
                      <div className="text-xs text-[rgba(48,56,60,0.55)]">{r.guest_email || r.guest_phone}</div>
                    </TableCell>
                    <TableCell className="text-sm">{fmtDateTime(r.reservation_date)}</TableCell>
                    <TableCell className="text-right font-num">{r.party_size}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-sm capitalize text-[rgba(48,56,60,0.7)]">{r.source}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" data-testid={`edit-reservation-${r.id}`} onClick={() => openEdit(r)} className="h-8 w-8 text-[#526E7E]">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" data-testid={`delete-reservation-${r.id}`} onClick={() => setDeleteId(r.id)} className="h-8 w-8 text-[#9a3a3a]">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-[#18344A]">{editingId ? "Edit reservation" : "New reservation"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="reservation-form">
            {!editingId && <BrandSelectField value={form.brand_id} onChange={set("brand_id")} />}
            <div className="space-y-1.5">
              <Label>Guest name</Label>
              <Input required data-testid="reservation-guest-name" value={form.guest_name} onChange={(e) => set("guest_name")(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.guest_email} onChange={(e) => set("guest_email")(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={form.guest_phone} onChange={(e) => set("guest_phone")(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Party size</Label>
                <Input type="number" min="1" data-testid="reservation-party-size" value={form.party_size} onChange={(e) => set("party_size")(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Date &amp; time</Label>
                <Input type="datetime-local" required data-testid="reservation-datetime" value={form.reservation_date} onChange={(e) => set("reservation_date")(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={set("status")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Source</Label>
                <Select value={form.source} onValueChange={set("source")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="reservation-form-submit" className="bg-[#18344A] text-white hover:bg-[#18344A]/90">
                {saving ? "Saving\u2026" : editingId ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete reservation?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-reservation" onClick={confirmDelete} className="bg-[#9a3a3a] text-white hover:bg-[#9a3a3a]/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
